/* ==========================================================================
   plan-selector.js
   Wires the 1 / 3 / 12-month plan options to the product form.

   Each <plan-option> renders one radio carrying that plan's variant id, product
   id and Recharge selling-plan id. On change we:
     1. move the .is-selected class (this is what expands the benefits list),
     2. write variant / product / selling_plan into every add-to-cart form,
     3. dispatch `plan-selector:change` for anything else that wants to react.

   Discounts are NOT handled here. The theme's own available-offers.js derives
   the code from the product handle and auto-applies it; smart-cart-custom.js
   puts it on the /checkout URL. Add the "Available offers" block to the product
   template so that runs — plan-selector.css hides its UI.

   Load from sections/main-product.liquid:
     <script src="{{ 'plan-selector.js' | asset_url }}" defer="defer"></script>
   ========================================================================== */

class PlanOption extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.plan-option__radio');
    if (!this.input || this.input.dataset.bound === 'true') return;
    this.input.dataset.bound = 'true';
    this.input.addEventListener('change', () => {
      if (this.input.checked) PlanSelector.select(this);
    });
  }
}

const PlanSelector = {
  select(option) {
    const group = option.dataset.planGroup;
    document
      .querySelectorAll('plan-option[data-plan-group="' + group + '"]')
      .forEach((el) => el.classList.toggle('is-selected', el === option));

    const data = option.querySelector('.plan-option__radio').dataset;

    // Each step is isolated: a failure in one must never stop the others.
    try {
      this.syncForm(data);
    } catch (err) {
      console.error('[plan-selector] syncForm failed', err);
    }

    document.dispatchEvent(
      new CustomEvent('plan-selector:change', {
        detail: {
          group: group,
          handle: data.handle,
          productId: data.productId,
          variantId: data.variantId,
          sellingPlanId: data.sellingPlanId,
          months: Number(data.months || 1),
          price: data.price,
          discountCode: data.discountCode || '',
        },
      })
    );
  },

  /* Write the selected plan into every add-to-cart form. Other scripts on this
     theme (subscription-widget.js, product-info.js) write the same inputs on
     their own events and can land after us, so re-assert on a short delay and
     again immediately before the form posts. */
  syncForm(data) {
    this.writeForm(data);
    setTimeout(() => this.writeForm(data), 60);
    setTimeout(() => this.writeForm(data), 400);
  },

  writeForm(data) {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');

    forms.forEach((form) => {
      form.querySelectorAll('input[name="id"]').forEach((input) => {
        if (data.variantId) input.value = data.variantId;
      });

      form.querySelectorAll('input[name="product-id"]').forEach((input) => {
        if (data.productId) input.value = data.productId;
      });

      let planInput = form.querySelector('input[name="selling_plan"]');
      if (data.sellingPlanId) {
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          form.appendChild(planInput);
        }
        planInput.value = data.sellingPlanId;
        planInput.id = 'selling_plan_id';
      } else if (planInput) {
        planInput.remove();
      }
    });

    document.querySelectorAll('input.product-variant-id').forEach((input) => {
      if (data.variantId) input.value = data.variantId;
    });

    if (window.__planSelectorDebug) {
      const now = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      console.log('[plan-selector] wrote', {
        wanted: data.variantId,
        now: now ? now.value : 'no id input found',
        forms: forms.length,
      });
    }
  },

  /* Re-assert the selected plan right before the cart request goes out. */
  guard() {
    if (this._guarded) return;
    this._guarded = true;

    const reassert = () => {
      const radio = document.querySelector('plan-option.is-selected .plan-option__radio');
      if (radio) this.writeForm(radio.dataset);
    };

    document.addEventListener(
      'submit',
      (e) => {
        if (e.target.matches && e.target.matches('form[action*="/cart/add"]')) reassert();
      },
      true
    );

    document.addEventListener(
      'click',
      (e) => {
        if (!e.target.closest) return;
        if (e.target.closest('button[type="submit"][name="add"], .product-form__submit')) {
          reassert();
        }
      },
      true
    );
  },

  init() {
    this.guard();
    document.querySelectorAll('plan-option').forEach((option) => {
      const input = option.querySelector('.plan-option__radio');
      if (input && input.checked) PlanSelector.select(option);
    });
  },
};

if (!customElements.get('plan-option')) {
  customElements.define('plan-option', PlanOption);
}

document.addEventListener('DOMContentLoaded', () => PlanSelector.init());
document.addEventListener('shopify:section:load', () => PlanSelector.init());

window.PlanSelector = PlanSelector;
